import { Bar, BarChart, CartesianGrid, LabelList, Legend, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useDrillDownDialog } from "../DrillDownProvider";
import { DrillDownConfig, ReferenceLineConfig } from "../types";

export type StackedBarSeries = {
    key: string;
    label: string;
    color: string;
};

type StackedBarChartProps = {
    points: Array<Record<string, string | number>>;
    series: Array<StackedBarSeries>;
    categoryKey: string;
    height?: number;
    valueFormatter?: (value: number) => string;
    referenceLine?: ReferenceLineConfig;
    showBarLabels?: boolean;
    // Mostra o TOTAL do empilhamento por cima de cada barra (soma das séries do ponto).
    showTotalLabel?: boolean;
    // Drill-down por barra: recebe o ponto clicado (com todas as chaves extra que o caller
    // tenha posto no point, ex.: personKey) e devolve a config da modal — null = não clicável.
    barDrillDown?: (point: Record<string, string | number>) => DrillDownConfig | null;
    // Barras horizontais: categorias no eixo vertical — para muitas categorias com rótulos longos
    // (ex.: grupos de anúncios), que em barras verticais se sobrepõem no eixo X.
    horizontalBars?: boolean;
};

const STACK_TOTAL_KEY = "__stackTotal";
export const HORIZONTAL_CATEGORY_AXIS_WIDTH = 220;

export const StackedBarChart = ({
    points,
    series,
    categoryKey,
    height = 360,
    valueFormatter,
    referenceLine,
    showBarLabels = false,
    showTotalLabel = false,
    barDrillDown,
    horizontalBars = false,
}: StackedBarChartProps) => {
    const drillDownDialog = useDrillDownDialog();
    const isDrillable = Boolean(barDrillDown && drillDownDialog);

    // Total por barra pré-calculado num campo próprio, mostrado por cima da última série (topo do empilhamento).
    const chartData = showTotalLabel
        ? points.map((point) => ({
              ...point,
              [STACK_TOTAL_KEY]: series.reduce((total, serie) => total + Number(point[serie.key] || 0), 0),
          }))
        : points;

    const openBarDrillDown = (barEntry: any) => {
        const point = barEntry?.payload;
        if (!point || !barDrillDown || !drillDownDialog) return;
        const config = barDrillDown(point);
        if (config) drillDownDialog.openDrillDown(config);
    };

    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart data={chartData} layout={horizontalBars ? "vertical" : "horizontal"} margin={{ top: 24, right: 24, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                {horizontalBars ? (
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                ) : (
                    <XAxis dataKey={categoryKey} tick={{ fontSize: 12 }} interval={0} angle={-20} textAnchor="end" height={60} />
                )}
                {horizontalBars ? (
                    <YAxis type="category" dataKey={categoryKey} tick={{ fontSize: 12 }} interval={0} width={HORIZONTAL_CATEGORY_AXIS_WIDTH} />
                ) : (
                    <YAxis tick={{ fontSize: 12 }} />
                )}
                {referenceLine && (
                    <ReferenceLine
                        y={referenceLine.value}
                        stroke={referenceLine.color || "#E25555"}
                        strokeDasharray="4 4"
                        label={
                            referenceLine.label
                                ? { value: referenceLine.label, position: "right", fontSize: 10, fill: referenceLine.color || "#E25555" }
                                : undefined
                        }
                    />
                )}
                <Tooltip formatter={(value) => (valueFormatter ? valueFormatter(Number(value)) : value)} />
                <Legend />
                {series.map((serie, seriesIndex) => (
                    <Bar
                        key={serie.key}
                        dataKey={serie.key}
                        name={serie.label}
                        stackId="stack"
                        fill={serie.color}
                        isAnimationActive={false}
                        onClick={isDrillable ? openBarDrillDown : undefined}
                        cursor={isDrillable ? "pointer" : undefined}
                    >
                        {showBarLabels && (
                            // Rótulo CENTRADO dentro de cada segmento (não "top"): em barras empilhadas os
                            // rótulos "top" sobrepõem-se (os segmentos a zero encostam o "0" ao segmento de baixo).
                            // Valores a zero não mostram rótulo.
                            <LabelList
                                dataKey={serie.key}
                                position="center"
                                fontSize={10}
                                fill="#fff"
                                formatter={(value: number) => {
                                    const numericValue = Number(value);
                                    if (!numericValue) return "";
                                    return valueFormatter ? valueFormatter(numericValue) : String(numericValue);
                                }}
                            />
                        )}
                        {showTotalLabel && seriesIndex === series.length - 1 && (
                            // Total do empilhamento por cima da última série (topo da barra).
                            <LabelList
                                dataKey={STACK_TOTAL_KEY}
                                position={horizontalBars ? "right" : "top"}
                                fontSize={11}
                                fontWeight={700}
                                fill="#555"
                                formatter={(value: number) => {
                                    const numericValue = Number(value);
                                    if (!numericValue) return "";
                                    return valueFormatter ? valueFormatter(numericValue) : String(numericValue);
                                }}
                            />
                        )}
                    </Bar>
                ))}
            </BarChart>
        </ResponsiveContainer>
    );
};
